import { useState, type SubmitEvent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { createProject } from "../../api/projectsApi";
import ErrorNotice from "../../errors/ErrorNotice";
import { appPaths } from "../../shared/routing/appPaths";
import { useSubmission } from "../../shared/state/useSubmission";
import AppButton from "../../ui/buttons/AppButton";
import Field from "../../ui/forms/Field";
import ModalShell from "../../ui/overlays/ModalShell";
import { FORM_CONTROL_CLASS } from "../../ui/styles/formStyles";
import { useProjects } from "../projectContext";

/**
 * "New project…" (web-app.md WA-5): a name, then the new project is the chosen one and its
 * Documents open, the first thing a project needs. Proposal: F1 leaves the next step open.
 */
export default function NewProjectDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectProject, reloadProjects } = useProjects();
  const [name, setName] = useState("");
  const { state, submit } = useSubmission();
  const pending = state.status === "submitting";
  const formId = "new-project-form";

  const handleSubmit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    submit(async () => {
      const project = await createProject(name.trim());
      reloadProjects();
      selectProject(project.projectId);
      onClose();
      void navigate(appPaths.sources());
    });
  };

  return (
    <ModalShell
      title={t("projects.create.title")}
      onClose={onClose}
      closeDisabled={pending}
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <AppButton variant="secondary" size="sm" disabled={pending} onClick={onClose}>{t("common.cancel")}</AppButton>
          <AppButton type="submit" form={formId} variant="primary" size="sm" disabled={pending || name.trim() === ""}>
            {pending ? t("login.pleaseWait") : t("projects.create.submit")}
          </AppButton>
        </div>
      }
    >
      <form id={formId} onSubmit={handleSubmit} className="space-y-3">
        <Field label={t("projects.create.name")} hint={t("projects.create.next")}>
          {(control) => (
            <input {...control} className={FORM_CONTROL_CLASS} value={name} required onChange={(event) => { setName(event.target.value); }} />
          )}
        </Field>
        {state.status === "failed" ? <ErrorNotice error={state.error} /> : null}
      </form>
    </ModalShell>
  );
}
